import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  OperationCompany,
  OperationDate,
  OperationFilter,
  OperationState,
} from "../../pages/ActivityPage/ui/ActivityTable/types/types";

export interface Filters {
  company: {
    changePending: boolean;
    applied: "ALL" | OperationCompany[] | null;
    notApplied: "ALL" | OperationCompany[] | null;
  };
  state: {
    changePending: boolean;
    applied: "ALL" | OperationState[] | null;
    notApplied: "ALL" | OperationState[] | null;
  };
  operation: {
    changePending: boolean;
    applied: "ALL" | OperationFilter[] | null;
    notApplied: "ALL" | OperationFilter[] | null;
  };
  date: {
    changePending: boolean;
    applied: OperationDate | null;
    notApplied: OperationDate | null;
    rangeApplied: (string | undefined)[];
    rangeNotApplied: (string | undefined)[];
  };
  dateBeyond24: {
    changePending: boolean;
    applied: OperationDate | null;
    notApplied: OperationDate | null;
    rangeApplied: (string | undefined)[];
    rangeNotApplied: (string | undefined)[];
  };
}

interface InitialState {
  openModal: boolean;
  filterSelected: "date" | "operation" | "state" | "company" | null;
  filters: Filters;
}

type FilterValue<K extends keyof Filters> = K extends "company"
  ? Filters["company"]["applied"]
  : K extends "state"
    ? Filters["state"]["applied"]
    : K extends "operation"
      ? Filters["operation"]["applied"]
      : K extends "date"
        ? Filters["date"]["applied"]
        : never;

const initialState: InitialState = {
  openModal: false,
  filterSelected: null,
  filters: {
    company: {
      changePending: false,
      applied: "ALL",
      notApplied: "ALL",
    },
    state: {
      changePending: false,
      applied: "ALL",
      notApplied: "ALL",
    },
    operation: {
      changePending: false,
      applied: "ALL",
      notApplied: "ALL",
    },
    date: {
      changePending: false,
      applied: "30",
      notApplied: "30",
      rangeApplied: [undefined, undefined],
      rangeNotApplied: [undefined, undefined],
    },
    dateBeyond24: {
      changePending: false,
      applied: "0",
      notApplied: "0",
      rangeApplied: [undefined, undefined],
      rangeNotApplied: [undefined, undefined],
    },
  },
};

const activityFiltersSlice = createSlice({
  name: "activityFilters",
  initialState,
  reducers: {
    setOpenModal(
      state,
      action: PayloadAction<{
        openModal: boolean;
        filterSelected: InitialState["filterSelected"];
      }>
    ) {
      state.openModal = action.payload.openModal;
      state.filterSelected = action.payload.filterSelected;
    },
    setFilterSelected(
      state,
      action: PayloadAction<InitialState["filterSelected"]>
    ) {
      state.filterSelected = action.payload;
    },
    setFilterApplied<K extends keyof Filters>(
      state: InitialState,
      action: PayloadAction<{ filter: K; value: FilterValue<K> }>
    ) {
      state.filters[action.payload.filter].applied = action.payload.value;
    },
    setFilterNotApplied<K extends keyof Filters>(
      state: InitialState,
      action: PayloadAction<{ filter: K; value: FilterValue<K> }>
    ) {
      state.filters[action.payload.filter].notApplied = action.payload.value;
    },
    setFilterDateRangeNotApplied(
      state,
      action: PayloadAction<{ value: (string | undefined)[] }>
    ) {
      state.filters["date"].rangeNotApplied = action.payload.value;
    },
    setFilterDateRangeApplied(
      state,
      action: PayloadAction<{ value: (string | undefined)[] }>
    ) {
      state.filters["date"].rangeApplied = action.payload.value;
    },
    setFilterDateBeyond24RangeNotApplied(
      state,
      action: PayloadAction<{ value: (string | undefined)[] }>
    ) {
      state.filters["dateBeyond24"].rangeNotApplied = action.payload.value;
    },
    setFilterDateBeyond24RangeApplied(
      state,
      action: PayloadAction<{ value: (string | undefined)[] }>
    ) {
      state.filters["dateBeyond24"].rangeApplied = action.payload.value;
    },
    setFilterChangePending(
      state,
      action: PayloadAction<{ filter: keyof Filters; value: boolean }>
    ) {
      state.filters[action.payload.filter].changePending = action.payload.value;
    },
    resetDateFilter(state) {
      state.filters["date"] = initialState.filters["date"];
    },
    resetDateBeyond24Filter(state) {
      state.filters["dateBeyond24"] = initialState.filters["dateBeyond24"];
    },
    resetAllFilters(state) {
      state.filters = initialState.filters;
    },
    applyChanges(state) {
      (Object.keys(state.filters) as (keyof Filters)[]).forEach((filterKey) => {
        const filter = state.filters[filterKey];

        if (filterKey === "date" || filterKey === "dateBeyond24") {
          state.filters[filterKey].applied = state.filters[filterKey].notApplied;
          state.filters[filterKey].rangeApplied = [
            ...state.filters[filterKey].rangeNotApplied,
          ];
        } else {
          state.filters[filterKey].applied =
            state.filters[filterKey].notApplied;
        }

        filter.changePending = false;
      });
    },
  },
});

export const {
  applyChanges,
  resetAllFilters,
  resetDateBeyond24Filter,
  resetDateFilter,
  setFilterApplied,
  setFilterChangePending,
  setFilterDateBeyond24RangeApplied,
  setFilterDateBeyond24RangeNotApplied,
  setFilterDateRangeApplied,
  setFilterDateRangeNotApplied,
  setFilterNotApplied,
  setFilterSelected,
  setOpenModal,
} = activityFiltersSlice.actions;

export default activityFiltersSlice.reducer;
